import { useEffect, useState } from 'react';
import { cn } from '~/lib/utils';

const SPINNER_SIZES = {
  sm: 'size-4 border-2',
  md: 'size-6 border-2',
  lg: 'size-8 border-[3px]',
} as const;

type SpinnerSize = keyof typeof SPINNER_SIZES;

/**
 * Bare animated spinner ring (FRONT-328). Compose it inline (buttons,
 * chips) or reach for Loader / PageLoader for labeled loading states.
 */
function Spinner({ size = 'md', className }: { size?: SpinnerSize; className?: string }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn(
        'rounded-full border-border border-t-primary animate-spin',
        SPINNER_SIZES[size],
        className,
      )}
    />
  );
}

/** Inline loading state for panels, cards, and drawers — the ring with an optional label next to it. */
function Loader({
  label,
  size = 'sm',
  className,
}: {
  label?: string;
  size?: SpinnerSize;
  className?: string;
}) {
  return (
    <div
      className={cn('flex items-center justify-center gap-2 p-4 text-muted-foreground', className)}
    >
      <Spinner size={size} />
      {label && <span className="text-xs">{label}</span>}
    </div>
  );
}

/**
 * Full-screen loading state for app-shell moments: auth resolving,
 * redirecting to login, conversation/onboarding hand-off. Renders as a
 * frosted overlay — translucent background + backdrop blur — so any
 * content underneath softens instead of flashing; over an empty page it
 * degrades to a plain quiet backdrop.
 */
function PageLoader({
  label,
  delayMs = 0,
}: {
  label?: string;
  /** Stay invisible this long, so a gate that clears quickly never flashes the overlay. */
  delayMs?: number;
}) {
  const [shown, setShown] = useState(delayMs <= 0);

  useEffect(() => {
    if (shown) return;
    const id = window.setTimeout(() => setShown(true), delayMs);
    return () => window.clearTimeout(id);
  }, [shown, delayMs]);

  if (!shown) return null;
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background/60 backdrop-blur-md animate-in fade-in duration-300 motion-reduce:animate-none">
      <Spinner size="lg" />
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
    </div>
  );
}

export { Spinner, Loader, PageLoader };
